import React, { useEffect, useState } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalProduct.module.sass";
import Icon from "../Icon";
import TextInput from "../TextInput";
import Card from "../Card";
import Dropdown from "../Dropdown";
import { uploadDocument, uploadProspectDocument } from "../../services/documents.service";


const ModalImportFile = ({ visible, onClose, isFirstConnexion = false, contractId, studentId, prospectId, onDocumentUploaded }) => {
  const [fileName, setFileName] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [isGenerationInProgress, setIsGenerationInProgress] = useState(false);
  const [documentType, setDocumentType] = useState("Autre");

  // Types de documents disponibles pour les prospects
  const documentTypes = [
    "Carte identité",
    "Passport", 
    "Curriculum vitae",
    "RIB",
    "Permis",
    "Carte vitale",
    "Justificatif de domicile",
    "Lettre de motivation",
    "Attestation RQTH",
    "Attestation de recensement",
    "Autre"
  ];


  // Reset the form when modal is closed
  useEffect(() => {
    if (!visible) {
      setFileName("");
      setFileObj(null);
      setIsGenerationInProgress(false);
      setDocumentType("Autre");
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-product");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  const handleFileChange = (e, type) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setFileObj(files[0]);
    if (!fileName) {
      setFileName(files[0].name.split('.').slice(0, -1).join('.'));
    }
  };
  
  const handleImportFile = async () => {
    if (!fileName || !fileObj || (!studentId && !prospectId)) return;

    try {
      setIsGenerationInProgress(true);
      
      let result;
      if (prospectId) {
        console.log("Importation du fichier :", fileName, fileObj, "pour le prospect ID:", prospectId);
        // Utiliser le service uploadProspectDocument pour créer le document prospect
        result = await uploadProspectDocument(prospectId, fileObj, documentType || fileName);
      } else {
        console.log("Importation du fichier :", fileName, fileObj, "pour l'étudiant ID:", studentId);
        // Utiliser le service uploadDocument pour créer le document étudiant
        result = await uploadDocument(studentId, fileObj, documentType || fileName);
      }
      
      if (result) {
        console.log("Document uploadé avec succès:", result);
        console.log(prospectId ? `Prospect ID: ${prospectId}` : `Student ID: ${studentId}`);
        console.log(fileObj);
        console.log(documentType);
        
        // Si la fonction onDocumentUploaded est fournie, l'appeler pour rafraîchir la liste
        if (typeof onDocumentUploaded === 'function') {
          onDocumentUploaded();
        }
        
        // Fermer la modale
        onClose();
      } else {
        console.error("Échec de l'upload du document");
        alert("Échec de l'upload du document. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur lors de l'importation du fichier :", error);
      alert("Erreur lors de l'upload du document: " + (error.message || "Erreur inconnue"));
    } finally {
      setIsGenerationInProgress(false);
    }
  };

  return createPortal(
    visible && (
      <div id="modal-product" className={styles.modal} onClick={isFirstConnexion ? null : undefined}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer} style={{ position: 'relative' }}>
          <Card
            className={cn(styles.card)}
            title="Importer un document"
            classTitle="title-green"
            style={{ minHeight: '600px', position: 'relative' }}
          >
            <div className={styles.description}>
              <div className={styles.field} style={{ position: 'relative', zIndex: 9999 }}>

                <TextInput
                  className={styles.field}
                  label="Nom du fichier"
                  name="fileName"
                  id="fileName"
                  type="text"
                  value={fileName}
                  onChange={(e) => {
                    // Récupérer uniquement la valeur du champ (pas l'événement complet)
                    const nameValue = typeof e === 'object' && e.target ? e.target.value : e;
                    setFileName(nameValue);
                  }}
                />
                
                <Dropdown
                  className={styles.field}
                  label="Type de document"
                  options={documentTypes}
                  value={documentType}
                  setValue={setDocumentType}
                />
                
                <div style={{ height: '24px' }}></div>

                <div className={styles.uploadZone}>
                  <input
                    type="file"
                    className={styles.fileInput}
                    onChange={(e) => handleFileChange(e, documentType)}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <span className={styles.uploadText}>
                    Glissez un fichier ici ou cliquez pour parcourir
                  </span>
                </div>

                {fileObj && (
                  <div className={styles.fileList}>
                    <div className={styles.fileItem}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileName}>
                          {fileObj.name.split('.').slice(0, -1).join('.')}
                          <span className={styles.fileExt}>.{fileObj.name.split('.').pop()}</span>
                        </span>
                      </div>
                      <button
                        className={styles.removeButton}
                        onClick={() => setFileObj(null)}
                        title="Supprimer"
                      >
                        <Icon name="close" size="20" />
                      </button>
                    </div>
                  </div>
                )}

              </div>
              <div className={styles.btnWrap}>
                <button
                  className="button-green"
                  onClick={handleImportFile}
                  disabled={!fileName || !fileObj}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isGenerationInProgress ? (
                    <>
                      <Icon name="download" size="16" />
                      Importation en cours...
                    </>
                  ) : (
                    <>
                      <Icon name="download" size="16" />
                      Importer
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalImportFile;
